import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  Snackbar,
  Alert,
  type AlertColor,
  type SnackbarProps,
  type SnackbarCloseReason,
} from "@mui/material";

interface NotificationContextType {
  notify: (
    message: string,
    severity?: AlertColor,
    autoHideDuration?: number,
    anchorOrigin?: SnackbarProps["anchorOrigin"],
  ) => void;
}

const NotificationContext = createContext<NotificationContextType>(null!);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState({
    open: false,
    message: "",
    severity: "success" as AlertColor,
    autoHideDuration: 4000,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    } as SnackbarProps["anchorOrigin"],
  });

  const notify = useCallback(
    (
      message: string,
      severity: AlertColor = "success",
      autoHideDuration = 4000,
      anchorOrigin: SnackbarProps["anchorOrigin"] = {
        vertical: "bottom",
        horizontal: "left",
      },
    ) => {
      setState({
        open: true,
        message,
        severity,
        autoHideDuration,
        anchorOrigin,
      });
    },
    [],
  );

  const handleClose = (_: unknown, reason?: SnackbarCloseReason) => {
    if (reason === "clickaway") return;
    setState((prev) => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={state.anchorOrigin}
      >
        <Alert
          onClose={() => handleClose(null)}
          severity={state.severity}
          variant="filled"
          elevation={6}
          sx={{ maxWidth: 420, fontWeight: 500 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error("useNotification must be used within NotificationProvider");
  return context;
};
